// SPDX-License-Identifier: Elastic-2.0
/**
 * useToast — consumer hook for the ToastProvider.
 *
 * Returns `{ show, dismiss }` bound to the nearest provider. Throws if
 * called outside a provider so misuse is loud during dev.
 *
 * The actual context + provider live in `ToastProvider.tsx`; this file
 * is split out so consumers can import the hook without pulling in the
 * provider's render tree.
 *
 * ESLint: `react-refresh/only-export-components` would normally
 * complain about non-component exports from a `.tsx` file — this is a
 * `.ts` file with no JSX, so it's safe to live alongside.
 */
import { createContext, useContext } from "react";

import type { ToastOptions } from "./Toast";

export interface ToastApi {
  /**
   * Enqueue a toast. Returns its assigned id so callers can
   * imperatively `dismiss(id)` later.
   */
  show: (opts: ToastOptions) => string;
  /** Dismiss a toast by id. No-op if the id is unknown. */
  dismiss: (id: string) => void;
}

/**
 * Internal context shared between `ToastProvider` and `useToast`. The
 * default value is `null` so the hook can detect "no provider mounted"
 * cleanly — see error in `useToast` below.
 */
export const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) {
    throw new Error(
      "useToast() called outside of <ToastProvider>. Wrap the app in <ToastProvider> (see web/src/components/FEEDBACK_INTEGRATION.md).",
    );
  }
  return api;
}
